import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { BsStars } from "react-icons/bs";
import {
  useGenerateVideoLinkMutation,
  useGenerateVideoPreviewsMutation,
  useGenerateVideoScriptMutation,
  useGetVideoStatusQuery,
  useRenderVideoMutation,
  useUpdateVideoLinkMutation,
} from "store/aiHub/aiGenerationApi";
import * as Yup from "yup";
import AvatarTabPanel from "./AvatarTabPanel";
import MusicTabPanel from "./MusicTabPanel";
import ProductUploadForm from "./ProductUploadForm";
import ScriptSelector from "./ScriptSelector";
import VideoPreviews from "./VideoPreviews";
import VideoSettingsForm from "./VideoSettingsForm";
import VoicesTabPanel from "./VoiceTabPanel";
import ManualUploadForm from "./ManualUploadForm";
import { isValidCustomScript, removeEmptyValues } from "utils/functions";
import { useNavigate } from "react-router-dom";

/* ---------- Helpers ---------- */

const fromApiToFormValues = (linkData) => ({
  brandName: linkData?.link?.title?.split(" ")[0] || "",
  description: linkData?.link?.description || "",
  logo_url: linkData?.link?.logo_url || "",
  id: linkData?.id || "",
  assets: [
    ...(linkData?.link?.image_urls || []).map((url) => ({
      url,
      type: "image",
      selected: true,
    })),
    ...(linkData?.link?.video_urls || []).map((url) => ({
      url,
      type: "video",
      selected: true,
    })),
  ],
});

/* ---------- Main Component ---------- */

const UploadVideoProductModal = ({ isOpen, onClose, videoUsageData }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const productUploadRef = useRef();
  const manualUploadRef = useRef();

  // Steps:
  // 0 = Link screen
  // 1 = Product/Manual form
  // 2 = Video settings
  // 3 = Script selection
  // 4 = Avatar/Voice/Music
  // 5 = Previews (Render)
  const [currentStep, setCurrentStep] = useState(0);

  const [videoLinkData, setVideoLinkData] = useState([null]);
  const [uploadType, setUploadType] = useState(""); // "manual" or ""
  const [videoUpdatedData, setVideoUpdatedData] = useState([]);
  const [generatedScriptData, setGeneratedScriptData] = useState([null]);

  const [formValues, setFormValues] = useState({ brandName: "", assets: [] });
  const [manualFormValues, setManualFormValues] = useState({
    brandName: "",
    assets: [],
    videoId: "",
    logo_url: "",
    description: "",
  });

  const [videoSettings, setVideoSettings] = useState({
    aspectRatio: "9:16",
    duration: "15",
    language: "en",
    targetAudience: "",
  });

  const [selectedScriptData, setSelectedScriptData] = useState({
    script: "",
    script_style: "",
  });

  const [selectedTab, setSelectedTab] = useState("avatar");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedVoiceAccent, setSelectedVoiceAccent] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState(null);

  const [previewId, sePreviewId] = useState(null);
  const [isStatusGenerating, setIsStatusGenerating] = useState(false);
  const [selectedPreviews, setSelectedPreviews] = useState([]);
  const [isRenderSubmitting, setIsRenderSubmitting] = useState(false);

  const tabIndexMap = { avatar: 0, voices: 1, music: 2 };
  const indexTabMap = { 0: "avatar", 1: "voices", 2: "music" };

  const [generateVideoLink, { isLoading: isLinkGenerating }] =
    useGenerateVideoLinkMutation();
  const [updateVideoLink, { isLoading: isLinkUpdating }] =
    useUpdateVideoLinkMutation();
  const [generateVideoScript, { isLoading: isScriptGenerating }] =
    useGenerateVideoScriptMutation();
  const [generateVideoPreviews] = useGenerateVideoPreviewsMutation();
  const { data: videoStatus, isLoading: isVideoStatusLoading, refetch: refetchVideoStatus, error: isVideoPreviewsError } =
    useGetVideoStatusQuery(
      { previewId },
      {
        skip: !previewId,
      }
    );
  const [renderVideo] = useRenderVideoMutation();

  // Basic retry while previews are getting prepared
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 4;
  const retryDelay = 12000;

  useEffect(() => {
    if (videoStatus && !isVideoStatusLoading) {
      if (videoStatus?.previews && videoStatus?.previews?.length === 0) {
        if (retryCount < maxRetries) {
          const timer = setTimeout(() => {
            refetchVideoStatus();
            setRetryCount((c) => c + 1);
          }, retryDelay);
          return () => clearTimeout(timer);
        } else {
          setIsStatusGenerating(false);
        }
      } else if (videoStatus?.previews?.length > 0) {
        setRetryCount(0);
        setIsStatusGenerating(false);
      }
    }
  }, [videoStatus, isVideoStatusLoading, retryCount, refetchVideoStatus]);

  /* ---------- Handlers ---------- */

  const handleNext = () => setCurrentStep((s) => s + 1);
  const handleBack = () => setCurrentStep((s) => s - 1);

  const handleModalClose = () => {
    // reset minimal state
    setCurrentStep(0);
    setUploadType("");
    setVideoLinkData([null]);
    setVideoUpdatedData([]);
    setGeneratedScriptData([null]);
    setFormValues({ brandName: "", assets: [] });
    setManualFormValues({ brandName: "", assets: [], videoId: "", logo_url: "", description: "" });
    setSelectedScriptData({ script: "", script_style: "" });
    setSelectedAvatar(null);
    setSelectedVoice(null);
    setSelectedVoiceAccent(null);
    setSelectedMusic(null);
    setSelectedPreviews([]);
    sePreviewId(null);
    setIsStatusGenerating(false);
    onClose();
  };

  const productLinkFormik = useFormik({
    initialValues: { productLink: "" },
    validationSchema: Yup.object({
      productLink: Yup.string().url("Enter a valid URL").required("Product link is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      handleNext();
      setUploadType("");

      generateVideoLink({ url: values.productLink })
        .unwrap()
        .then((response) => {
          const responseData = fromApiToFormValues(response);
          setVideoLinkData(responseData);
        })
        .catch((error) => {
          handleBack();
          toast({
            title: "Link analysis failed",
            description: error?.data?.message || "Something went wrong",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        })
        .finally(() => setSubmitting(false));
    },
  });

  const handleFormSubmit = (vals) => {
    handleNext();

    const selectedAssets = vals?.assets?.filter((a) => a.selected);
    const image_urls = selectedAssets.filter((a) => a.type === "image").map((a) => a.url);
    const video_urls = selectedAssets.filter((a) => a.type === "video").map((a) => a.url);

    const initialPayload = {
      title: vals?.brandName,
      ...(image_urls.length > 0 && { image_urls }),
      ...(video_urls.length > 0 && { video_urls }),
      logo_url: vals?.logo_url || "",
      description: vals?.description || "",
    };

    const payload = removeEmptyValues(initialPayload);
    updateVideoLink({ payload, linkId: videoLinkData?.id })
      .unwrap()
      .then((response) => setVideoUpdatedData(response))
      .catch((error) => {
        handleBack();
        toast({
          title: "Failed to update",
          status: "error",
          description: error?.data?.message || "Something went wrong",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      });
  };

  const handleManualSubmit = (vals) => {
    handleNext();

    const selectedAssets = vals?.assets?.filter((a) => a.selected);
    const image_urls = selectedAssets.filter((a) => a.type === "image").map((a) => a.url);
    const video_urls = selectedAssets.filter((a) => a.type === "video").map((a) => a.url);

    const initialPayload = {
      title: vals?.brandName,
      ...(image_urls.length > 0 && { image_urls }),
      ...(video_urls.length > 0 && { video_urls }),
      logo_url: vals?.logo_url || "",
      description: vals?.description || "",
    };

    const payload = removeEmptyValues(initialPayload);
    updateVideoLink({ payload, linkId: vals?.videoId })
      .unwrap()
      .then((response) => setVideoUpdatedData(response))
      .catch((error) => {
        handleBack();
        toast({
          title: "Failed to update",
          status: "error",
          description: error?.data?.message || error?.message || "Something went wrong",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      });
  };

  const handleGenerateScript = () => {
    handleNext();
    setIsStatusGenerating(true);

    const initialPayload = {
      url: videoUpdatedData?.url,
      title: videoUpdatedData?.title,
      description: videoUpdatedData?.description,
      language: videoSettings?.language,
      video_length: videoSettings?.duration,
      target_audience: videoSettings?.targetAudience,
      script_styles: ["BenefitsV2", "CallToActionV2", "GenzWriter", "ProductHighlightsV2", "StoryTimeWriter"],
    };

    const payload = removeEmptyValues(initialPayload);
    generateVideoScript(payload)
      .unwrap()
      .then((response) => setGeneratedScriptData(response))
      .catch((error) => {
        handleBack();
        setIsStatusGenerating(false);
        toast({
          title: "Failed to generate",
          status: "error",
          description: error?.data?.message || error?.message || "Something went wrong",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      });
  };

  const handleGenerateVideoPreviews = () => {
    handleNext();

    const videoLink = uploadType === "manual" ? manualFormValues?.videoId : videoLinkData?.id;

    const initialPayload = {
      name: videoUpdatedData?.title,
      target_platform: "facebook",
      target_audience: videoSettings?.targetAudience,
      language: videoSettings?.language,
      video_length: videoSettings?.duration,
      aspect_ratio: videoSettings?.aspectRatio?.replace(":", "x"),
      override_avatar: selectedAvatar?.id,
      override_voice: selectedVoiceAccent?.id,
      override_script: selectedScriptData?.script,
      background_music_url: selectedMusic?.url,
      background_music_volume: 0.5,
      voiceover_volume: 0.5,
      link: videoLink,
      no_background_music: false,
      no_caption: false,
      no_emotion: false,
      caption_style: "normal-black",
      caption_offset_x: 0.5,
      caption_offset_y: 0.5,
      caption_setting: {
        style: "normal-black",
        offset: { x: 0, y: 0.4 },
        font_family: "Montserrat",
        font_size: 40,
        font_style: "italic",
        background_color: "#FFFFFFFF",
        text_color: "#000000FF",
        highlight_text_color: "#007BFFFF",
        text_shadow: "0px 1px 2px rgba(0, 0, 0, 0.25)",
        hidden: false,
      },
      visual_styles: [
        "DynamicProductTemplate",
        "GreenScreenEffectTemplate",
        "QuickTransitionTemplate",
        "SimpleAvatarOverlayTemplate",
        "VanillaTemplate",
        "LegoScriptNegativeHook",
        "LegoScriptAmazedHook",
        "LegoScriptCannotBelieveHook",
        "LegoScriptPremiumFeelingHook",
        "LegoScriptMissingOutAlertHook",
      ],
    };

    const payload = removeEmptyValues(initialPayload);
    generateVideoPreviews(payload)
      .unwrap()
      .then((response) => {
        sePreviewId(response?.id);
        setIsStatusGenerating(true);
      })
      .catch((error) => {
        handleBack();
        toast({
          title: "Failed to generate",
          status: "error",
          description: error?.data?.message || "Something went wrong",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      });
  };

  const handlePreviewCheckboxChange = (media_job) => {
    setSelectedPreviews((prev) =>
      prev.includes(media_job) ? prev.filter((id) => id !== media_job) : [...prev, media_job]
    );
  };

  // Direct render: render once per selected preview, then close parent modal
  const handleConfirmRender = async () => {
    try {
      if (!selectedPreviews?.length) {
        toast({
          title: "No previews selected",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      setIsRenderSubmitting(true);

      await Promise.all(
        selectedPreviews.map((media_job) =>
          renderVideo({ payload: { media_job }, id: videoStatus?.id }).unwrap()
        )
      );

      toast({
        title: `Render started for ${selectedPreviews.length} video${selectedPreviews.length > 1 ? "s" : ""}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Close the parent modal directly
      handleModalClose();
      navigate("/generate?tab=videos&vtab=rendered");
      
    } catch (error) {
      toast({
        title: "Failed to start rendering",
        status: "error",
        description: error?.data?.message || error?.message || "Something went wrong",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsRenderSubmitting(false);
    }
  };

  /* ---------- Render ---------- */

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        size={{ base: "6xl" }}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent minH="80vh">
          <ModalHeader>
            <ModalCloseButton />
            <Flex gap={2} direction={"column"}>
              <Flex alignItems={"center"} gap={2}>
                <BsStars />
                <Text>AI Video Generation</Text>
                <Tooltip
                  borderRadius={5}
                  label={
                    <Flex direction={"column"} gap={2} fontSize="12px" p="5px">
                      <Text fontWeight={"bold"}>AI Video Generation</Text>
                      <Text>
                        AI-generated video ads are created using your product information and visuals. By assembling product
                        highlights, voiceovers, and dynamic visuals, the AI helps generate high-performing video ads optimised
                        for various platforms.
                      </Text>
                      <Flex direction={"column"} gap={2}>
                        <Text fontWeight={"bold"}>Types of AI Variations:</Text>
                        <Text>Product URL Based: Extracts product details from your URL to auto-generate video content.</Text>
                        <Text>Asset Based: Uses your uploaded visuals to create fully edited video ads.</Text>
                      </Flex>
                      <Text>Note: Hustle does not guarantee the completeness, accuracy, or reliability of AI-generated videos.</Text>
                    </Flex>
                  }
                  fontSize="md"
                >
                  <InfoOutlineIcon pointerEvents="all" />
                </Tooltip>
              </Flex>
            </Flex>
          </ModalHeader>

          <Divider mb={"10px"} />

          <ModalBody>
            <Flex direction={"row"} minH={"65vh"}>
              {/* STEP 0: Product link */}
              {currentStep === 0 && (
                <Flex width="full" alignItems={"center"} justifyContent={"center"}>
                  <form onSubmit={productLinkFormik.handleSubmit}>
                    <Flex direction={"column"} gap={6} w="100%" maxW="4xl">
                      <Heading fontSize={"3xl"}>Share your product link to generate a video</Heading>
                      <Flex direction={"column"} gap={1}>
                        <Input
                          placeholder="e.g. amazon product link, shopify product link, app store link, etc."
                          size="md"
                          borderRadius="md"
                          name="productLink"
                          value={productLinkFormik.values.productLink}
                          onChange={productLinkFormik.handleChange}
                          onBlur={productLinkFormik.handleBlur}
                        />
                        {productLinkFormik.touched.productLink && productLinkFormik.errors.productLink ? (
                          <Text color="red.500" fontSize={"14px"}>
                            {productLinkFormik.errors.productLink}
                          </Text>
                        ) : null}
                      </Flex>

                      <Flex alignItems={"center"} justifyContent={"center"} gap={4}>
                        <Button
                          type="button"
                          size="md"
                          onClick={() => {
                            handleNext();
                            setUploadType("manual");
                          }}
                        >
                          Upload manually
                        </Button>
                        <Button colorScheme="brand" type="submit" size="md" isLoading={productLinkFormik.isSubmitting}>
                          Analyze URL
                        </Button>
                      </Flex>
                    </Flex>
                  </form>
                </Flex>
              )}

              {/* STEP 1: Product/Manual form */}
              {currentStep === 1 && (
                <Flex width="full" alignItems={"center"} justifyContent={"center"}>
                  {uploadType === "manual" ? (
                    <ManualUploadForm
                      ref={manualUploadRef}
                      isLoading={isLinkGenerating}
                      onSubmit={handleManualSubmit}
                      onFormUpdate={(values) => setManualFormValues(values)}
                    />
                  ) : (
                    <ProductUploadForm
                      ref={productUploadRef}
                      isLoading={isLinkGenerating}
                      initialValues={videoLinkData}
                      onSubmit={handleFormSubmit}
                      onFormUpdate={(values) => setFormValues(values)}
                    />
                  )}
                </Flex>
              )}

              {/* STEP 2: Settings */}
              {currentStep === 2 && (
                <>
                  {isLinkUpdating ? (
                    <Flex direction={"column"} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                        <Image src="/Ai_ImageLoader.gif" alt="generating..." boxSize="200px" objectFit="contain" width="100%" />
                        <Flex direction={"column"} alignItems={"center"} gap={2}>
                            <Text fontWeight={"bold"} fontSize={"14px"}>
                                Loading video settings...
                            </Text>
                        </Flex>
                    </Flex>
                  ) : (
                    <Flex width="full" alignItems={"center"} justifyContent={"center"}>
                      <VideoSettingsForm
                        values={videoSettings}
                        onChange={setVideoSettings}
                        aiTargetAudiences={videoUpdatedData?.ai_target_audiences}
                      />
                    </Flex>
                  )}
                </>
              )}

              {/* STEP 3: Script selection */}
              {currentStep === 3 && (
                <>
                  {isScriptGenerating ? (
                    <Flex direction={"column"} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                      <Image src="/Ai_ImageLoader.gif" alt="generating..." boxSize="200px" objectFit="contain" width="100%" />
                      <Flex direction={"column"} alignItems={"center"} gap={2}>
                        <Text fontWeight={"bold"} fontSize={"14px"}>
                          AI generating scripts...
                        </Text>
                        <Text fontSize={"14px"}>This step may take up to 45 seconds</Text>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex width="full" justifyContent={"center"}>
                      <ScriptSelector
                        generatedScripts={generatedScriptData?.generated_scripts || []}
                        onSelect={(data) => setSelectedScriptData(data)}
                      />
                    </Flex>
                  )}
                </>
              )}

              {/* STEP 4: Avatar / Voices / Music */}
              {currentStep === 4 && (
                <Flex width="full" justifyContent={"center"}>
                  <Box width="840px" maxH="500px" display="flex" flexDirection="column">
                    <Heading fontSize="2xl" mb={2}>
                      Choose Avatar, Voice, Music
                    </Heading>
                    <Tabs
                      variant={"enclosed-colored"}
                      colorScheme="brand"
                      index={tabIndexMap[selectedTab]}
                      onChange={(index) => setSelectedTab(indexTabMap[index])}
                      isLazy
                    >
                      <TabList>
                        <Tab fontSize={"14px"}>Avatar</Tab>
                        <Tab fontSize={"14px"}>Voices</Tab>
                        <Tab fontSize={"14px"}>Music</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel>
                          <Flex justifyContent={"center"}>
                            <AvatarTabPanel selectedAvatar={selectedAvatar} onSelectAvatar={setSelectedAvatar} />
                          </Flex>
                        </TabPanel>
                        <TabPanel>
                          <Flex justifyContent={"center"}>
                            <VoicesTabPanel
                              selectedTab={selectedTab}
                              selectedVoice={selectedVoice}
                              onSelectVoice={setSelectedVoice}
                              selectedVoiceAccent={selectedVoiceAccent}
                              onSelectAccent={setSelectedVoiceAccent}
                            />
                          </Flex>
                        </TabPanel>
                        <TabPanel>
                          <Flex justifyContent={"center"}>
                            <MusicTabPanel selectedTab={selectedTab} selectedMusic={selectedMusic} onSelectMusic={setSelectedMusic} />
                          </Flex>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </Box>
                </Flex>
              )}

              {/* STEP 5: Previews (Render) */}
              {currentStep === 5 && (
                <>
                  {isVideoPreviewsError ? (
                    <Flex direction="column" align="center" justify="center" h="60vh" gap={2} textAlign="center" px={4}>
                      <Text fontSize="lg" fontWeight="bold" color="red.500">
                        {isVideoPreviewsError.data?.message || "Failed to load previews"}
                      </Text>
                    </Flex>
                  ) : isStatusGenerating ? (
                    <Flex direction={"column"} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                      <Image src="/Ai_ImageLoader.gif" alt="generating..." boxSize="200px" objectFit="contain" width="100%" />
                      <Flex direction={"column"} alignItems={"center"} gap={2}>
                        <Text fontWeight={"bold"} fontSize={"14px"}>
                          AI generating creative avatars...
                        </Text>
                        <Text fontSize={"14px"}>This step may take up to a minute</Text>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex width="full" justifyContent={"center"}>
                      <VideoPreviews
                        previewData={videoStatus}
                        selectedPreviews={selectedPreviews}
                        onSelectedPreviews={handlePreviewCheckboxChange}
                      />
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Flex width="100%" justifyContent="space-between" alignItems="center">
              <Flex />
              <Flex gap={2}>
                {currentStep === 0 && <Flex gap={2}>{/* First step has no Back button */}</Flex>}

                {currentStep === 1 && (
                  <Flex gap={2}>
                    <Button colorScheme="gray" variant="solid" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                    {uploadType === "manual" ? (
                      <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={() => manualUploadRef?.current?.submitForm()}
                        isDisabled={
                          !manualFormValues.brandName ||
                          !manualFormValues.videoId ||
                          manualFormValues.assets?.length < 3 ||
                          manualFormValues.assets.filter((a) => a.selected).length < 3
                        }
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={() => productUploadRef?.current?.submitForm()}
                        isDisabled={
                          !formValues.brandName ||
                          formValues.assets?.length < 3 ||
                          formValues.assets.filter((a) => a.selected).length < 3
                        }
                      >
                        Next
                      </Button>
                    )}
                  </Flex>
                )}

                {currentStep === 2 && (
                  <Flex gap={2}>
                    <Button colorScheme="gray" variant="solid" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                    <Button colorScheme="brand" size="sm" onClick={handleGenerateScript}>
                      Next
                    </Button>
                  </Flex>
                )}

                {currentStep === 3 && (
                  <Flex gap={2}>
                    <Button colorScheme="gray" variant="solid" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      colorScheme="brand"
                      size="sm"
                      isDisabled={
                        selectedScriptData.script_style === "custom"
                          ? !isValidCustomScript(selectedScriptData?.script)
                          : selectedScriptData.script.trim() === "" || isScriptGenerating
                      }
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </Flex>
                )}

                {currentStep === 4 && (
                  <Flex gap={2}>
                    <Button colorScheme="gray" variant="solid" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                    <Button colorScheme="brand" size="sm" onClick={handleGenerateVideoPreviews}>
                      Next
                    </Button>
                  </Flex>
                )}

                {currentStep === 5 && (
                  <Flex gap={2}>
                    <Button colorScheme="gray" variant="solid" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      colorScheme="brand"
                      size="sm"
                      isDisabled={selectedPreviews?.length === 0}
                      isLoading={isRenderSubmitting}
                      onClick={handleConfirmRender}
                    >
                      {`Render (${selectedPreviews?.length} video${selectedPreviews?.length > 1 ? "s" : ""})`}
                    </Button>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UploadVideoProductModal;